import React, { useState } from "react";
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Box,
  Typography,
  IconButton,
  Chip,
  Avatar,
  Divider,
  Tabs,
  Tab,
  Tooltip,
  Stack,
  useTheme,
} from "@mui/material";
import {
  Close as CloseIcon,
  VideoCall as VideoCallIcon,
  Event as EventIcon,
  AccessTime as TimeIcon,
  Person as PersonIcon,
  People as PeopleIcon,
  Description as DocumentIcon,
  Launch as LaunchIcon,
  Edit as EditIcon,
  PersonAdd as PersonAddIcon,
  Delete as DeleteIcon,
  ContentCopy as CopyIcon,
  Check as CheckIcon,
} from "@mui/icons-material";
import HtmlViewer from "./HtmlViewer";

export default function MeetingDetailsModal({
  open,
  onClose,
  meeting,
  onEdit,
  onOpenDocument,
  onAddPeople,
  onRemoveParticipant,
  onDelete,
}) {
  const theme = useTheme();
  const isDark = theme.palette.mode === "dark";
  const [activeTab, setActiveTab] = useState(0);
  const [copiedLink, setCopiedLink] = useState(false);

  if (!meeting) return null;

  const str = meeting.meeting_datetime ? String(meeting.meeting_datetime).trim() : "";
  const isoStr = str.includes(" ") ? str.replace(" ", "T") : str;
  const meetingDate = isoStr ? new Date(isoStr) : null;
  const validDate = meetingDate && !isNaN(meetingDate.getTime()) ? meetingDate : null;

  const isPast = validDate ? validDate < new Date() : false;
  const isToday = validDate
    ? validDate.toDateString() === new Date().toDateString()
    : false;

  let statusLabel = isPast ? "Completed / Past" : isToday ? "Today" : "Upcoming";
  let statusColor = isPast ? "default" : isToday ? "warning" : "success";

  const handleCopyLink = () => {
    if (!meeting.meeting_link) return;
    navigator.clipboard.writeText(meeting.meeting_link);
    setCopiedLink(true);
    setTimeout(() => setCopiedLink(false), 2000);
  };

  return (
    <Dialog
      open={open}
      onClose={onClose}
      maxWidth="md"
      fullWidth
      PaperProps={{
        sx: {
          borderRadius: "20px",
          bgcolor: isDark ? "#111c44" : "#ffffff",
          backgroundImage: "none",
          border: isDark ? "1px solid rgba(255, 255, 255, 0.08)" : "1px solid #e2e8f0",
          boxShadow: isDark
            ? "0 25px 50px -12px rgba(0, 0, 0, 0.7)"
            : "0 25px 50px -12px rgba(112, 144, 176, 0.25)",
          overflow: "hidden",
        },
      }}
    >
      {/* HEADER */}
      <DialogTitle
        sx={{
          p: 3,
          pb: 2,
          borderBottom: isDark ? "1px solid rgba(255, 255, 255, 0.08)" : "1px solid #eef2f6",
          bgcolor: isDark ? "rgba(255, 255, 255, 0.02)" : "rgba(244, 247, 254, 0.5)",
        }}
      >
        <Box sx={{ display: "flex", alignItems: "flex-start", justifyContent: "space-between", gap: 2 }}>
          <Box sx={{ display: "flex", alignItems: "flex-start", gap: 2 }}>
            <Box
              sx={{
                width: 48,
                height: 48,
                borderRadius: "14px",
                background: "linear-gradient(135deg, #f59e0b 0%, #d97706 100%)",
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                color: "#ffffff",
                boxShadow: "0 4px 12px rgba(245, 158, 11, 0.35)",
                flexShrink: 0,
                mt: 0.5,
              }}
            >
              <VideoCallIcon sx={{ fontSize: 26 }} />
            </Box>
            <Box>
              <Box sx={{ display: "flex", alignItems: "center", gap: 1.5, flexWrap: "wrap", mb: 0.8 }}>
                <Typography
                  variant="h6"
                  sx={{
                    fontWeight: 700,
                    color: isDark ? "#ffffff" : "#1b2559",
                    fontSize: { xs: "1.05rem", sm: "1.25rem" },
                    lineHeight: 1.3,
                  }}
                >
                  {meeting.title}
                </Typography>
                <Chip
                  label={statusLabel}
                  size="small"
                  color={statusColor}
                  sx={{
                    height: 22,
                    fontSize: "0.72rem",
                    fontWeight: 700,
                    borderRadius: "6px",
                  }}
                />
              </Box>

              <Stack
                direction="row"
                spacing={2.5}
                flexWrap="wrap"
                useFlexGap
                sx={{
                  color: isDark ? "#a3aed0" : "#707eae",
                  fontSize: "0.82rem",
                  rowGap: 0.8,
                }}
              >
                <Box sx={{ display: "flex", alignItems: "center", gap: 0.6 }}>
                  <EventIcon sx={{ fontSize: 16, color: "#f59e0b" }} />
                  <span>
                    {validDate
                      ? validDate.toLocaleDateString("en-US", {
                          weekday: "short",
                          month: "short",
                          day: "numeric",
                          year: "numeric",
                        })
                      : "Date TBD"}
                  </span>
                </Box>
                <Box sx={{ display: "flex", alignItems: "center", gap: 0.6 }}>
                  <TimeIcon sx={{ fontSize: 16, color: "#f59e0b" }} />
                  <span>
                    {validDate
                      ? validDate.toLocaleTimeString("en-US", {
                          hour: "numeric",
                          minute: "2-digit",
                          hour12: true,
                        })
                      : "Time TBD"}
                  </span>
                </Box>
                <Box sx={{ display: "flex", alignItems: "center", gap: 0.6 }}>
                  <PersonIcon sx={{ fontSize: 16, color: "#a3aed0" }} />
                  <span>Created by {meeting.creator_name || "Admin"}</span>
                </Box>
              </Stack>
            </Box>
          </Box>

          <IconButton
            size="small"
            onClick={onClose}
            sx={{
              color: isDark ? "#a3aed0" : "#707eae",
              bgcolor: isDark ? "rgba(255, 255, 255, 0.05)" : "#f4f7fe",
              "&:hover": {
                bgcolor: isDark ? "rgba(255, 255, 255, 0.1)" : "#e2e8f0",
              },
            }}
          >
            <CloseIcon fontSize="small" />
          </IconButton>
        </Box>

        {/* ACTION BUTTONS BAR */}
        <Box sx={{ display: "flex", alignItems: "center", gap: 1.5, mt: 2.5, flexWrap: "wrap" }}>
          {meeting.meeting_link && (
            <>
              <Button
                variant="contained"
                size="small"
                startIcon={<LaunchIcon fontSize="small" />}
                href={
                  meeting.meeting_link.startsWith("http")
                    ? meeting.meeting_link
                    : `https://${meeting.meeting_link}`
                }
                target="_blank"
                rel="noopener noreferrer"
                sx={{
                  textTransform: "none",
                  fontWeight: 700,
                  fontSize: "0.82rem",
                  borderRadius: "10px",
                  background: "linear-gradient(135deg, #f59e0b 0%, #d97706 100%)",
                  color: "#ffffff",
                  boxShadow: "0 4px 12px rgba(245, 158, 11, 0.3)",
                  "&:hover": {
                    background: "linear-gradient(135deg, #d97706 0%, #b45309 100%)",
                  },
                }}
              >
                Join Meeting
              </Button>

              <Button
                variant="outlined"
                size="small"
                startIcon={copiedLink ? <CheckIcon fontSize="small" /> : <CopyIcon fontSize="small" />}
                onClick={handleCopyLink}
                sx={{
                  textTransform: "none",
                  fontWeight: 600,
                  fontSize: "0.82rem",
                  borderRadius: "10px",
                  borderColor: isDark ? "rgba(255, 255, 255, 0.15)" : "#cbd5e1",
                  color: isDark ? "#e2e8f0" : "#475569",
                  "&:hover": {
                    borderColor: "#f59e0b",
                    color: "#f59e0b",
                  },
                }}
              >
                {copiedLink ? "Link Copied!" : "Copy Link"}
              </Button>
            </>
          )}

          <Button
            variant="outlined"
            size="small"
            startIcon={<PersonAddIcon fontSize="small" />}
            onClick={() => onAddPeople(meeting)}
            sx={{
              textTransform: "none",
              fontWeight: 600,
              fontSize: "0.82rem",
              borderRadius: "10px",
              borderColor: isDark ? "rgba(255, 255, 255, 0.15)" : "#cbd5e1",
              color: isDark ? "#e2e8f0" : "#475569",
              "&:hover": {
                borderColor: "#3b82f6",
                color: "#3b82f6",
              },
            }}
          >
            Add People
          </Button>

          <Button
            variant="outlined"
            size="small"
            startIcon={<DocumentIcon fontSize="small" />}
            onClick={() => onOpenDocument(meeting)}
            sx={{
              textTransform: "none",
              fontWeight: 600,
              fontSize: "0.82rem",
              borderRadius: "10px",
              borderColor: isDark ? "rgba(255, 255, 255, 0.15)" : "#cbd5e1",
              color: isDark ? "#e2e8f0" : "#475569",
              "&:hover": {
                borderColor: "#8b5cf6",
                color: "#8b5cf6",
              },
            }}
          >
            {meeting.document_content ? "Edit MOM / Notes" : "Prepare Document"}
          </Button>

          <Button
            variant="outlined"
            size="small"
            startIcon={<EditIcon fontSize="small" />}
            onClick={() => onEdit(meeting)}
            sx={{
              textTransform: "none",
              fontWeight: 600,
              fontSize: "0.82rem",
              borderRadius: "10px",
              borderColor: isDark ? "rgba(255, 255, 255, 0.15)" : "#cbd5e1",
              color: isDark ? "#e2e8f0" : "#475569",
              "&:hover": {
                borderColor: "#f59e0b",
                color: "#f59e0b",
              },
            }}
          >
            Edit Info
          </Button>
        </Box>

        {/* TABS */}
        <Tabs
          value={activeTab}
          onChange={(e, val) => setActiveTab(val)}
          sx={{
            mt: 2.5,
            minHeight: 40,
            "& .MuiTab-root": {
              textTransform: "none",
              fontWeight: 700,
              fontSize: "0.875rem",
              minHeight: 40,
              color: isDark ? "#a3aed0" : "#707eae",
              "&.Mui-selected": {
                color: "#f59e0b",
              },
            },
            "& .MuiTabs-indicator": {
              backgroundColor: "#f59e0b",
              height: 3,
              borderRadius: "3px 3px 0 0",
            },
          }}
        >
          <Tab
            label={
              <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                <DocumentIcon sx={{ fontSize: 18 }} />
                <span>Minutes & Agenda Document</span>
                {meeting.document_content && (
                  <Chip
                    label="Ready"
                    size="small"
                    sx={{
                      height: 18,
                      fontSize: "0.68rem",
                      fontWeight: 700,
                      bgcolor: "rgba(16, 185, 129, 0.15)",
                      color: "#10b981",
                      borderRadius: "4px",
                    }}
                  />
                )}
              </Box>
            }
          />
          <Tab
            label={
              <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                <PeopleIcon sx={{ fontSize: 18 }} />
                <span>Attendees ({meeting.participants?.length || 0})</span>
              </Box>
            }
          />
        </Tabs>
      </DialogTitle>

      {/* CONTENT */}
      <DialogContent sx={{ p: { xs: 2, sm: 3 }, minHeight: 340 }}>
        {activeTab === 0 ? (
          <Box>
            <HtmlViewer
              title={meeting.document_title || `${meeting.title} - Meeting Minutes`}
              htmlContent={meeting.document_content}
              updatedAt={meeting.document_updated_at}
              onEdit={() => onOpenDocument(meeting)}
              showActions={true}
            />
          </Box>
        ) : (
          <Box sx={{ display: "flex", flexDirection: "column", gap: 2 }}>
            <Box
              sx={{
                display: "flex",
                alignItems: "center",
                justifyContent: "space-between",
                flexWrap: "wrap",
                gap: 1,
              }}
            >
              <Typography
                variant="subtitle2"
                sx={{
                  fontWeight: 700,
                  color: isDark ? "#ffffff" : "#1b2559",
                  fontSize: "0.95rem",
                }}
              >
                Confirmed Attendees ({meeting.participants?.length || 0})
              </Typography>
              <Button
                size="small"
                variant="text"
                startIcon={<PersonAddIcon fontSize="small" />}
                onClick={() => onAddPeople(meeting)}
                sx={{
                  textTransform: "none",
                  fontWeight: 700,
                  color: "#f59e0b",
                  "&:hover": { bgcolor: "rgba(245, 158, 11, 0.08)" },
                }}
              >
                + Add More Attendees
              </Button>
            </Box>

            {!meeting.participants || meeting.participants.length === 0 ? (
              <Box
                sx={{
                  p: 5,
                  textAlign: "center",
                  color: isDark ? "#a3aed0" : "#707eae",
                  bgcolor: isDark ? "rgba(255, 255, 255, 0.02)" : "#f8fafc",
                  borderRadius: "14px",
                  border: isDark ? "1px dashed rgba(255, 255, 255, 0.12)" : "1px dashed #cbd5e1",
                }}
              >
                <PeopleIcon sx={{ fontSize: 40, opacity: 0.4, mb: 1 }} />
                <Typography variant="body2" sx={{ fontWeight: 600 }}>
                  No participants added to this meeting yet.
                </Typography>
                <Typography variant="caption" sx={{ color: isDark ? "#707eae" : "#94a3b8" }}>
                  Click "+ Add More Attendees" to invite your colleagues.
                </Typography>
              </Box>
            ) : (
              <Box
                sx={{
                  display: "grid",
                  gridTemplateColumns: { xs: "1fr", sm: "repeat(2, 1fr)" },
                  gap: 1.5,
                  maxHeight: 380,
                  overflowY: "auto",
                  pr: 0.5,
                }}
              >
                {meeting.participants.map((p) => {
                  const name = p.employee_name || p.email;
                  return (
                    <Box
                      key={p.employee_id}
                      sx={{
                        display: "flex",
                        alignItems: "center",
                        justifyContent: "space-between",
                        p: 1.5,
                        borderRadius: "12px",
                        bgcolor: isDark ? "rgba(255, 255, 255, 0.03)" : "#ffffff",
                        border: isDark
                          ? "1px solid rgba(255, 255, 255, 0.07)"
                          : "1px solid #eef2f6",
                        boxShadow: isDark ? "none" : "0 2px 6px rgba(112, 144, 176, 0.06)",
                        transition: "all 0.2s ease",
                        "&:hover": {
                          borderColor: "#f59e0b",
                        },
                      }}
                    >
                      <Box sx={{ display: "flex", alignItems: "center", gap: 1.5, minWidth: 0 }}>
                        <Avatar
                          src={p.profile_pic_url}
                          alt={name}
                          sx={{
                            width: 38,
                            height: 38,
                            fontSize: "0.85rem",
                            fontWeight: 700,
                            bgcolor: "#f59e0b",
                            color: "#ffffff",
                          }}
                        >
                          {name.charAt(0).toUpperCase()}
                        </Avatar>
                        <Box sx={{ minWidth: 0 }}>
                          <Typography
                            variant="body2"
                            noWrap
                            sx={{
                              fontWeight: 700,
                              color: isDark ? "#ffffff" : "#1b2559",
                              fontSize: "0.875rem",
                            }}
                          >
                            {name}
                          </Typography>
                          <Typography
                            variant="caption"
                            noWrap
                            sx={{
                              display: "block",
                              color: isDark ? "#a3aed0" : "#707eae",
                              fontSize: "0.75rem",
                            }}
                          >
                            {p.designation_name || p.department_name || p.email}
                          </Typography>
                        </Box>
                      </Box>

                      {onRemoveParticipant && (
                        <Tooltip title="Remove Attendee">
                          <IconButton
                            size="small"
                            onClick={() => onRemoveParticipant(meeting.id, p.employee_id)}
                            sx={{
                              color: "#ef4444",
                              opacity: 0.65,
                              "&:hover": {
                                opacity: 1,
                                bgcolor: "rgba(239, 68, 68, 0.1)",
                              },
                            }}
                          >
                            <CloseIcon fontSize="small" />
                          </IconButton>
                        </Tooltip>
                      )}
                    </Box>
                  );
                })}
              </Box>
            )}
          </Box>
        )}
      </DialogContent>

      {/* FOOTER */}
      <DialogActions
        sx={{
          px: 3,
          py: 2,
          borderTop: isDark ? "1px solid rgba(255, 255, 255, 0.08)" : "1px solid #eef2f6",
          bgcolor: isDark ? "rgba(255, 255, 255, 0.02)" : "#f8fafc",
          display: "flex",
          justifyContent: "space-between",
        }}
      >
        <Button
          color="error"
          size="small"
          startIcon={<DeleteIcon fontSize="small" />}
          onClick={() => onDelete(meeting.id)}
          sx={{
            textTransform: "none",
            fontWeight: 700,
            borderRadius: "10px",
            "&:hover": { bgcolor: "rgba(239, 68, 68, 0.1)" },
          }}
        >
          Delete Meeting
        </Button>
        <Button
          onClick={onClose}
          variant="outlined"
          sx={{
            textTransform: "none",
            fontWeight: 700,
            borderRadius: "10px",
            borderColor: isDark ? "rgba(255, 255, 255, 0.2)" : "#cbd5e1",
            color: isDark ? "#ffffff" : "#1b2559",
            "&:hover": {
              borderColor: "#f59e0b",
              color: "#f59e0b",
            },
          }}
        >
          Close
        </Button>
      </DialogActions>
    </Dialog>
  );
}
